import {
  Flex,
  Stack,
  Box,
  Text,
  Button,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react';
import { HelivanColor } from '../helivan/HelivanColor';
import { HelivanText } from '../helivan/HelivanText';
import { StyledSelect } from '../helivan/StyledSelect';
import { useState, useMemo } from 'react';
import Plot from 'react-plotly.js';
import { Data, Layout } from 'plotly.js';

// Mock data generator
const generateMockInfluencers = (count: number) => {
  const names = [
    'Emma Johnson',
    'Liam Smith',
    'Olivia Brown',
    'Noah Davis',
    'Ava Wilson',
    'Sophia Martinez',
    'Jackson Taylor',
    'Isabella Anderson',
    'Lucas Thomas',
    'Mia Garcia',
    'Aiden White',
    'Harper Harris',
    'Ethan Martin',
    'Amelia Thompson',
    'Mason Robinson',
    'Abigail Lewis',
    'Elijah Walker',
    'Emily Young',
    'James Allen',
    'Charlotte King',
    'Benjamin Wright',
    'Sofia Scott',
    'Logan Green',
    'Avery Baker',
    'Alexander Hall',
  ];

  const genders = ['Male', 'Female', 'Non-binary'];

  return Array.from({ length: count }, (_, i) => {
    const randomName = names[Math.floor(Math.random() * names.length)];
    const handle =
      '@' +
      randomName.toLowerCase().replace(' ', '_') +
      Math.floor(Math.random() * 1000);
    return {
      id: i,
      name: randomName,
      handle: handle,
      followers: Math.floor(Math.random() * 10000000) + 10000,
      gender: genders[Math.floor(Math.random() * genders.length)],
      x: Math.random() * 100,
      y: Math.random() * 100,
    };
  });
};

// Generate mock followers
const generateMockFollowers = (count: number) => {
  const niches = [
    'Fitness',
    'Beauty',
    'Comedy',
    'Fashion',
    'Travel',
    'Food',
    'Tech',
    'Gaming',
    'Music',
    'Art',
  ];
  const locations = [
    'United States',
    'United Kingdom',
    'Canada',
    'Australia',
    'Germany',
    'France',
    'Japan',
    'Brazil',
    'India',
    'Mexico',
  ];
  const genders = ['Male', 'Female', 'Non-binary'];
  const ageRanges = ['13-17', '18-24', '25-34', '35-44', '45-54', '55+'];

  return Array.from({ length: count }, (_, i) => {
    // Random number of niches (1-3)
    const nicheCount = Math.floor(Math.random() * 3) + 1;
    const userNiches = [];

    // Select random niches without duplicates
    const availableNiches = [...niches];
    for (let j = 0; j < nicheCount; j++) {
      const index = Math.floor(Math.random() * availableNiches.length);
      userNiches.push(availableNiches[index]);
      availableNiches.splice(index, 1);
    }

    return {
      id: i,
      niches: userNiches,
      location: locations[Math.floor(Math.random() * locations.length)],
      gender: genders[Math.floor(Math.random() * genders.length)],
      age: ageRanges[Math.floor(Math.random() * ageRanges.length)],
      x: Math.random() * 100,
      y: Math.random() * 100,
    };
  });
};

// Calculate distance between two points
const calculateDistance = (x1: number, y1: number, x2: number, y2: number) => {
  return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
};

// Format large numbers (e.g., 1.2M for followers)
const formatNumber = (num: number) => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
};

export const SocialMedia = () => {
  const [influencers] = useState(() => generateMockInfluencers(100));
  const [selectedInfluencer, setSelectedInfluencer] = useState<number | null>(
    null
  );
  const [showFollowerMap, setShowFollowerMap] = useState(false);
  const [followers] = useState(() => generateMockFollowers(200));
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Calculate nearest neighbors when an influencer is selected
  const nearestNeighbors = useMemo(() => {
    if (selectedInfluencer === null) return [];

    const selected = influencers[selectedInfluencer];

    return influencers
      .map((inf, idx) => ({
        ...inf,
        distance: calculateDistance(selected.x, selected.y, inf.x, inf.y),
        similarityScore: Math.floor(Math.random() * 30) + 70, // Random score between 70-100
        idx,
      }))
      .filter((inf) => inf.id !== selected.id)
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 5);
  }, [selectedInfluencer, influencers]);

  // Use all influencers (StyledSelect handles filtering internally)
  const filteredInfluencers = influencers;

  // Prepare plot data
  const plotData = useMemo(() => {
    const baseData: Data = {
      x: influencers.map((inf) => inf.x),
      y: influencers.map((inf) => inf.y),
      mode: 'markers',
      type: 'scatter',
      marker: {
        size: 10,
        color: influencers.map((_, i) =>
          selectedInfluencer === i
            ? HelivanColor.text.blueDark
            : HelivanColor.text.gray
        ),
        opacity: influencers.map((_, i) =>
          selectedInfluencer === null ||
          selectedInfluencer === i ||
          nearestNeighbors.some((n) => n.idx === i)
            ? 1
            : 0.3
        ),
      },
      text: influencers.map(
        (inf) =>
          `Name: ${inf.name}<br>Handle: ${inf.handle}<br>Followers: ${formatNumber(inf.followers)}<br>Gender: ${inf.gender}`
      ),
      hoverinfo: 'text',
    };

    return [baseData];
  }, [influencers, selectedInfluencer, nearestNeighbors]);

  // Prepare follower plot data
  const followerPlotData = useMemo(() => {
    // Generate different colors based on niches
    const nicheColors: Record<string, string> = {
      Fitness: '#FF5733',
      Beauty: '#FFC300',
      Comedy: '#36D7B7',
      Fashion: '#C70039',
      Travel: '#8E44AD',
      Food: '#3498DB',
      Tech: '#2ECC71',
      Gaming: '#F1C40F',
      Music: '#E74C3C',
      Art: '#9B59B6',
    };

    // Group followers by their primary niche for coloring
    const baseData: Data = {
      x: followers.map((follower) => follower.x),
      y: followers.map((follower) => follower.y),
      mode: 'markers',
      type: 'scatter',
      marker: {
        size: 8,
        color: followers.map(
          (follower) =>
            nicheColors[follower.niches[0]] || HelivanColor.text.gray
        ),
      },
      text: followers.map(
        (follower) =>
          `Niches: ${follower.niches.join(', ')}<br>Location: ${follower.location}<br>Gender: ${follower.gender}<br>Age: ${follower.age}`
      ),
      hoverinfo: 'text',
    };

    return [baseData];
  }, [followers]);

  // Calculate follower demographics breakdown
  const followerBreakdown = useMemo(() => {
    if (followers.length === 0)
      return {
        niches: [] as Array<{
          name: string;
          percentage: number;
          count: number;
        }>,
        genders: [] as Array<{ name: string; percentage: number }>,
        ages: [] as Array<{ name: string; percentage: number }>,
        locations: [] as Array<{ name: string; percentage: number }>,
      };

    const nichesCount: Record<string, number> = {};
    const gendersCount: Record<string, number> = {};
    const agesCount: Record<string, number> = {};
    const locationsCount: Record<string, number> = {};

    // Count all occurrences
    followers.forEach((follower) => {
      // Count each niche separately
      follower.niches.forEach((niche) => {
        nichesCount[niche] = (nichesCount[niche] || 0) + 1;
      });

      // Count demographics
      gendersCount[follower.gender] = (gendersCount[follower.gender] || 0) + 1;
      agesCount[follower.age] = (agesCount[follower.age] || 0) + 1;
      locationsCount[follower.location] =
        (locationsCount[follower.location] || 0) + 1;
    });

    // Convert to percentages for single-value demographics
    const calculatePercentages = (
      counts: Record<string, number>,
      total: number
    ) => {
      return Object.entries(counts)
        .map(([key, count]) => ({
          name: key,
          percentage: Math.round((count / total) * 100),
        }))
        .sort((a, b) => b.percentage - a.percentage);
    };

    // For niches, calculate percentage of followers in each niche
    const nicheBreakdown = Object.entries(nichesCount)
      .map(([niche, count]) => ({
        name: niche,
        count,
        percentage: Math.round((count / followers.length) * 100),
      }))
      .sort((a, b) => b.count - a.count);

    return {
      niches: nicheBreakdown,
      genders: calculatePercentages(gendersCount, followers.length),
      ages: calculatePercentages(agesCount, followers.length),
      locations: calculatePercentages(locationsCount, followers.length).slice(
        0,
        5
      ), // Top 5 locations
    };
  }, [followers]);

  const plotLayout: Partial<Layout> = {
    title: { text: 'Influencer Map' },
    showlegend: false,
    hovermode: 'closest',
    margin: { l: 40, r: 40, b: 40, t: 60 },
    paper_bgcolor: HelivanColor.background.primary,
    plot_bgcolor: HelivanColor.background.secondary,
    font: {
      color: HelivanColor.text.black,
    },
    xaxis: {
      showgrid: false,
      zeroline: false,
      showticklabels: false,
    },
    yaxis: {
      showgrid: false,
      zeroline: false,
      showticklabels: false,
    },
    autosize: true,
  };

  const followerPlotLayout: Partial<Layout> = {
    title: { text: 'Follower Map' },
    showlegend: false,
    hovermode: 'closest',
    margin: { l: 40, r: 40, b: 40, t: 60 },
    paper_bgcolor: HelivanColor.background.primary,
    plot_bgcolor: HelivanColor.background.secondary,
    font: {
      color: HelivanColor.text.black,
    },
    xaxis: {
      showgrid: false,
      zeroline: false,
      showticklabels: false,
    },
    yaxis: {
      showgrid: false,
      zeroline: false,
      showticklabels: false,
    },
    autosize: true,
  };

  // Render the lookalike tab content
  const renderLookalikeTab = () => {
    return (
      <Stack p={4} spacing={4} width="full" overflow="scroll">
        <Flex width="full" direction={{ base: 'column', md: 'row' }} gap={4}>
          <Box
            flex="3"
            p={4}
            borderRadius="md"
            bg={HelivanColor.background.secondary}
            height="500px"
            width="100%"
          >
            <Plot
              data={plotData}
              layout={plotLayout}
              style={{ width: '100%', height: '100%' }}
              config={{ responsive: true }}
              useResizeHandler={true}
            />
          </Box>

          <Flex flex="1" direction="column" gap={4} minWidth="300px">
            <Box
              p={4}
              borderRadius="md"
              bg={HelivanColor.background.secondary}
              width="100%"
            >
              <HelivanText variant="largeSubheader" mb={2}>
                Select Influencer
              </HelivanText>
              <Box position="relative" width="100%">
                <StyledSelect
                  options={filteredInfluencers.map((inf) => ({
                    value: inf.id.toString(),
                    label: `${inf.name} (${inf.handle})`,
                  }))}
                  value={selectedInfluencer?.toString() || null}
                  onChange={(value) => {
                    setSelectedInfluencer(value ? parseInt(value) : null);
                    setShowFollowerMap(false);
                  }}
                  placeholder="Choose an influencer"
                  isSearchable={true}
                />
              </Box>

              {selectedInfluencer !== null && (
                <Button
                  mt={3}
                  width="100%"
                  colorScheme="blue"
                  bg={HelivanColor.text.helivanBlue}
                  color="white"
                  onClick={() => setShowFollowerMap(!showFollowerMap)}
                >
                  {showFollowerMap ? 'Hide' : 'Show'} Follower Breakdown
                </Button>
              )}
            </Box>

            {selectedInfluencer !== null && (
              <Box
                p={4}
                borderRadius="md"
                bg={HelivanColor.background.secondary}
                width="100%"
              >
                <HelivanText variant="largeSubheader" mb={2}>
                  Similar Influencers to {influencers[selectedInfluencer].name}
                </HelivanText>
                <Stack spacing={3} width="100%">
                  {nearestNeighbors.map((neighbor) => (
                    <Flex
                      key={neighbor.id}
                      p={3}
                      borderRadius="md"
                      bg={HelivanColor.background.primary}
                      justifyContent="space-between"
                      alignItems="center"
                      width="100%"
                    >
                      <Box>
                        <HelivanText variant="largeBody">
                          {neighbor.name}
                        </HelivanText>
                        <Text fontSize="sm" color={HelivanColor.text.gray}>
                          {neighbor.handle} • {formatNumber(neighbor.followers)}{' '}
                          followers
                        </Text>
                      </Box>
                      <Box
                        bg={HelivanColor.hover.lightBlue}
                        color={HelivanColor.text.black}
                        borderRadius="full"
                        px={3}
                        py={1}
                      >
                        {neighbor.similarityScore}% similar
                      </Box>
                    </Flex>
                  ))}
                </Stack>
              </Box>
            )}
          </Flex>
        </Flex>

        {selectedInfluencer !== null && showFollowerMap && (
          <Flex width="full" direction={{ base: 'column', md: 'row' }} gap={4}>
            <Box
              p={4}
              borderRadius="md"
              bg={HelivanColor.background.secondary}
              height="400px"
              width={{ base: '100%', md: '70%' }}
            >
              <Plot
                data={followerPlotData}
                layout={followerPlotLayout}
                style={{ width: '100%', height: '100%' }}
                config={{ responsive: true }}
                useResizeHandler={true}
              />
            </Box>

            <Flex
              direction="column"
              gap={4}
              width={{ base: '100%', md: '30%' }}
              minWidth="300px"
            >
              <Box
                p={4}
                borderRadius="md"
                bg={HelivanColor.background.secondary}
                width="100%"
              >
                <HelivanText variant="largeSubheader" mb={3}>
                  Follower Demographics
                </HelivanText>

                <Box mb={4}>
                  <HelivanText variant="subheader" mb={1}>
                    Interests (% of followers)
                  </HelivanText>
                  <Text fontSize="xs" color={HelivanColor.text.gray} mb={2}>
                    Note: Followers may have multiple interests
                  </Text>
                  {followerBreakdown.niches.map((niche) => (
                    <Flex
                      key={niche.name}
                      justifyContent="space-between"
                      mb={1}
                    >
                      <Text>{niche.name}</Text>
                      <Flex>
                        <Text fontWeight="bold" mr={1}>
                          {niche.percentage}%
                        </Text>
                        <Text
                          fontSize="xs"
                          color={HelivanColor.text.gray}
                          alignSelf="flex-end"
                        >
                          ({niche.count})
                        </Text>
                      </Flex>
                    </Flex>
                  ))}
                </Box>

                <Box mb={4}>
                  <HelivanText variant="subheader" mb={1}>
                    Gender
                  </HelivanText>
                  {followerBreakdown.genders.map((gender) => (
                    <Flex
                      key={gender.name}
                      justifyContent="space-between"
                      mb={1}
                    >
                      <Text>{gender.name}</Text>
                      <Text fontWeight="bold">{gender.percentage}%</Text>
                    </Flex>
                  ))}
                </Box>

                <Box mb={4}>
                  <HelivanText variant="subheader" mb={1}>
                    Age
                  </HelivanText>
                  {followerBreakdown.ages.map((age) => (
                    <Flex key={age.name} justifyContent="space-between" mb={1}>
                      <Text>{age.name}</Text>
                      <Text fontWeight="bold">{age.percentage}%</Text>
                    </Flex>
                  ))}
                </Box>

                <Box>
                  <HelivanText variant="subheader" mb={1}>
                    Top Locations
                  </HelivanText>
                  {followerBreakdown.locations.map((location) => (
                    <Flex
                      key={location.name}
                      justifyContent="space-between"
                      mb={1}
                    >
                      <Text>{location.name}</Text>
                      <Text fontWeight="bold">{location.percentage}%</Text>
                    </Flex>
                  ))}
                </Box>
              </Box>
            </Flex>
          </Flex>
        )}
      </Stack>
    );
  };

  // Render the create campaign tab content
  const renderCreateCampaignTab = () => {
    // Mock campaign data
    const mockCampaigns = [
      {
        id: 1,
        title: 'Summer Collection Gifting',
        brand: 'Lululemon',
        status: 'Active',
        creatorsFound: 107,
        startDate: '2023-06-15',
        budget: '$25,000',
      },
      {
        id: 2,
        title: 'TikTok Paid Campaign',
        brand: 'GoGo squeeZ Active',
        status: 'Active',
        creatorsFound: 10,
        startDate: '2023-11-01',
        budget: '$15,000',
      },
      {
        id: 3,
        title: 'Blush Launch Campaign',
        brand: 'Milani Cosmetics',
        status: 'Completed',
        creatorsFound: 210,
        startDate: '2023-03-10',
        endDate: '2023-04-15',
        budget: '$30,000',
      },
      {
        id: 4,
        title: 'TikTok Paid Campaign',
        brand: 'IMPOSSIBLE',
        status: 'Completed',
        creatorsFound: 20,
        startDate: '2023-02-01',
        endDate: '2023-03-15',
        budget: '$12,000',
      },
    ];

    return (
      <Flex width="full" direction="column" p={4}>
        <Flex justifyContent="space-between" alignItems="center" mb={6}>
          <HelivanText variant="largeSubheader">Your Campaigns</HelivanText>
          <Button
            colorScheme="blue"
            bg={HelivanColor.text.helivanBlue}
            color="white"
            onClick={() => setIsModalOpen(true)}
          >
            Create Batch
          </Button>
        </Flex>

        <Box
          width="100%"
          bg={HelivanColor.background.secondary}
          borderRadius="md"
          overflow="hidden"
        >
          <Flex
            bg={HelivanColor.background.lightestGrey}
            p={4}
            borderBottom="1px solid"
            borderColor={HelivanColor.border.primary}
            fontWeight="bold"
          >
            <Box width="35%">Campaign</Box>
            <Box width="25%">Brand</Box>
            <Box width="20%">Status</Box>
            <Box width="20%">Creators</Box>
          </Flex>

          {mockCampaigns.map((campaign) => (
            <Flex
              key={campaign.id}
              p={4}
              borderBottom="1px solid"
              borderColor={HelivanColor.border.primary}
              _hover={{ bg: HelivanColor.hover.lightBlue }}
              cursor="pointer"
            >
              <Box width="35%">
                <Text fontWeight="medium">{campaign.title}</Text>
                <Text fontSize="sm" color={HelivanColor.text.gray}>
                  {campaign.status === 'Active'
                    ? `Started ${campaign.startDate}`
                    : `${campaign.startDate} - ${campaign.endDate}`}
                </Text>
              </Box>
              <Box width="25%">{campaign.brand}</Box>
              <Box width="20%">
                <Box
                  display="inline-block"
                  px={2}
                  py={1}
                  borderRadius="full"
                  bg={
                    campaign.status === 'Active'
                      ? HelivanColor.hover.lightBlue
                      : HelivanColor.background.lightestGrey
                  }
                  color={
                    campaign.status === 'Active'
                      ? HelivanColor.text.helivanBlue
                      : HelivanColor.text.gray
                  }
                  fontSize="sm"
                >
                  {campaign.status}
                </Box>
              </Box>
              <Box width="20%">{campaign.creatorsFound} creators</Box>
            </Flex>
          ))}
        </Box>
      </Flex>
    );
  };

  return (
    <Flex
      height="full"
      width="full"
      direction="column"
      gap={0}
      overflow="hidden"
      bg={HelivanColor.background.primary}
      border="1px solid"
      borderColor={HelivanColor.border.primary}
      borderRadius="8px"
    >
      <Flex
        width="full"
        alignItems="center"
        py="16px"
        px="30px"
        height="fit-content"
        borderBottom="1px solid"
        borderBottomColor={HelivanColor.border.primary}
        position="sticky"
      >
        <HelivanText variant="largeSubheader">Social Media</HelivanText>
      </Flex>

      <Tabs width="full" colorScheme="blue" overflow="hidden">
        <TabList px={4} borderBottomColor={HelivanColor.border.primary}>
          <Tab
            _selected={{
              color: HelivanColor.text.helivanBlue,
              borderBottomColor: HelivanColor.text.helivanBlue,
            }}
          >
            Lookalike
          </Tab>
          <Tab
            _selected={{
              color: HelivanColor.text.helivanBlue,
              borderBottomColor: HelivanColor.text.helivanBlue,
            }}
          >
            Create Batch
          </Tab>
        </TabList>

        <TabPanels height="calc(100% - 40px)" overflow="auto">
          <TabPanel p={0} height="full">
            {renderLookalikeTab()}
          </TabPanel>
          <TabPanel p={0} height="full">
            {renderCreateCampaignTab()}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Flex>
  );
};
